import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Loader2, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminGetMedia, adminUploadMedia, mediaUrl } from "@/lib/api";
import { toast } from "sonner";

// A dialog to pick an existing media item or upload a new one. onSelect(mediaId).
export const MediaPickerDialog = ({ open, onOpenChange, onSelect }) => {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { data: media = [], isLoading } = useQuery({ queryKey: ["admin-media"], queryFn: adminGetMedia, enabled: open });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const m = await adminUploadMedia(file);
      await qc.invalidateQueries({ queryKey: ["admin-media"] });
      toast.success("Uploaded");
      onSelect(m.id);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text)" }}>
        <DialogHeader><DialogTitle>Select or Upload Logo</DialogTitle></DialogHeader>
        <div className="flex items-center justify-between">
          <p className="text-xs" style={{ color: "var(--muted)" }}>SVG, PNG, JPG or WEBP — optimised automatically.</p>
          <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="media-picker-upload-button"
            className="inline-flex items-center gap-2 rounded-[10px] px-3.5 py-2 text-sm font-medium" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload New
          </button>
          <input ref={inputRef} type="file" accept=".svg,.png,.jpg,.jpeg,.webp,image/*" className="hidden" onChange={handleUpload} />
        </div>
        <div className="mt-2 grid max-h-[52vh] grid-cols-3 gap-3 overflow-y-auto sm:grid-cols-4">
          <button onClick={() => { onSelect(null); onOpenChange(false); }} className="flex aspect-square flex-col items-center justify-center gap-1 rounded-[12px] border border-dashed text-xs" style={{ borderColor: "var(--border)", color: "var(--muted)" }}>
            <X size={16} /> No Logo
          </button>
          {isLoading && <p className="col-span-full py-6 text-center text-sm" style={{ color: "var(--muted)" }}>Loading…</p>}
          {media.map((m) => (
            <button key={m.id} onClick={() => { onSelect(m.id); onOpenChange(false); }} data-testid="media-picker-item"
              className="group relative aspect-square overflow-hidden rounded-[12px] border transition-colors"
              style={{ background: "var(--bg-elev-1)", borderColor: "var(--border)" }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--accent)")}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--border)")}>
              <img src={mediaUrl(m.thumb_url || m.url)} alt={m.filename} className="h-full w-full object-contain p-3" />
              <span className="absolute inset-x-0 bottom-0 truncate px-2 py-1 text-[10px]" style={{ background: "rgba(0,0,0,0.5)", color: "var(--text)" }}>{m.filename}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
